import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import claraOuvert from '../assets/clara_ouvert.png';
import logoBrandStarter from '../assets/logo_brandstarter.png';
import claraLeve from '../assets/clara_leve.png';
import claraCroise from '../assets/clara_croise.png';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,500&family=Inter:wght@400;500;600;700;800&display=swap');

  :root {
    --navy: #173B34;
    --navy-deep: #0E2820;
    --gold: #E4572E;
    --gold-soft: rgba(228,87,46,0.35);
    --blue-pale: #F6F2EC;
    --white: #FFFFFF;
    --ink: #222222;
    --ink-soft: #5B6B79;
    --line: #EDE9E2;
    --radius: 18px;
    --maxw: 1180px;
  }

  .lp * { box-sizing: border-box; margin: 0; padding: 0; }
  .lp { font-family: 'Inter', sans-serif; color: var(--ink); background: var(--white); -webkit-font-smoothing: antialiased; overflow-x: hidden; }
  .lp img { max-width: 100%; display: block; }
  .lp h1, .lp h2, .lp h3 { font-family: 'Playfair Display', serif; font-weight: 600; letter-spacing: -0.01em; color: var(--navy-deep); }
  .lp .wrap { max-width: var(--maxw); margin: 0 auto; padding: 0 24px; }
  .lp a { color: inherit; text-decoration: none; }
  .lp button { font-family: inherit; cursor: pointer; border: none; background: none; }

  /* NAV */
  .lp .nav { position: fixed; top: 0; left: 0; right: 0; z-index: 1000; background: rgba(255,255,255,0.92); backdrop-filter: saturate(180%) blur(10px); border-bottom: 1px solid var(--line); }
  .lp .nav-inner { max-width: var(--maxw); margin: 0 auto; padding: 0 20px; height: 64px; display: flex; align-items: center; justify-content: space-between; }
  .lp .logo { display: flex; align-items: center; gap: 10px; font-weight: 700; font-size: 18px; color: var(--navy-deep); }
  .lp .logo-mark { width: 34px; height: 34px; border-radius: 10px; background: var(--navy); display: flex; align-items: center; justify-content: center; color: var(--gold); font-family: 'Playfair Display', serif; font-weight: 700; font-size: 18px; flex-shrink: 0; }
  .lp .logo-word { font-family: 'Playfair Display', serif; font-weight: 600; color: var(--navy-deep); }
  .lp footer .logo-word { color: var(--white); }
  .lp .logo-word .accent { color: var(--gold); }
  .lp .nav-links { display: flex; align-items: center; gap: 28px; }
  .lp .nav-link { font-size: 14.5px; font-weight: 500; color: var(--ink-soft); }
  .lp .nav-link:hover { color: var(--navy); }
  .lp .nav-actions { display: flex; align-items: center; gap: 14px; }
  .lp .btn-ghost { font-size: 14.5px; font-weight: 600; color: var(--navy-deep); padding: 9px 6px; background: none; border: none; cursor: pointer; }
  .lp .btn-gold { background: var(--gold); color: #fff; padding: 11px 20px; border-radius: 999px; font-weight: 700; font-size: 14.5px; white-space: nowrap; box-shadow: 0 1px 0 rgba(0,0,0,0.04); transition: transform .15s ease, box-shadow .15s ease; display: inline-block; }
  .lp .btn-gold:hover { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(228,87,46,0.35); }
  .lp .btn-navy { background: var(--navy-deep); color: var(--white); padding: 14px 26px; border-radius: 999px; font-weight: 700; font-size: 15px; display: inline-flex; align-items: center; gap: 8px; transition: transform .15s ease, box-shadow .15s ease; cursor: pointer; border: none; }
  .lp .btn-navy:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(14,40,32,0.28); }
  .lp .btn-outline { border: 1.5px solid var(--navy-deep); color: var(--navy-deep); padding: 13px 24px; border-radius: 999px; font-weight: 600; font-size: 15px; display: inline-flex; align-items: center; gap: 8px; }

  /* HERO */
  .lp .hero { position: relative; padding: 128px 0 0 0; background: radial-gradient(720px 420px at 86% 8%, rgba(212,175,55,0.16), transparent 60%), linear-gradient(180deg, var(--blue-pale) 0%, #ffffff 78%); overflow: hidden; }
  .lp .hero-grid { display: grid; grid-template-columns: 1.05fr 0.95fr; gap: 40px; align-items: end; max-width: var(--maxw); margin: 0 auto; padding: 0 24px; }
  .lp .badge { display: inline-flex; align-items: center; gap: 8px; background: var(--navy-deep); color: #fff; padding: 8px 16px 8px 10px; border-radius: 999px; font-size: 13px; font-weight: 700; letter-spacing: .01em; margin-bottom: 26px; }
  .lp .badge .dot { width: 7px; height: 7px; border-radius: 50%; background: var(--gold); flex-shrink: 0; }
  .lp .hero h1 { font-size: clamp(34px, 5vw, 58px); line-height: 1.04; margin-bottom: 22px; }
  .lp .hero h1 .underline { position: relative; display: inline-block; }
  .lp .hero h1 .underline svg { position: absolute; left: 0; bottom: -6px; width: 100%; height: 10px; }
  .lp .hero p.lead { font-size: 18px; line-height: 1.6; color: var(--ink-soft); max-width: 480px; margin-bottom: 34px; }
  .lp .hero-ctas { display: flex; align-items: center; gap: 18px; flex-wrap: wrap; margin-bottom: 18px; }
  .lp .hero-sub { font-size: 13.5px; color: var(--ink-soft); display: flex; align-items: center; gap: 8px; padding-bottom: 40px; }
  .lp .hero-figure { position: relative; display: flex; justify-content: center; align-items: flex-end; height: 100%; }
  .lp .hero-figure img { width: 100%; max-width: 430px; filter: drop-shadow(0 30px 40px rgba(23,59,52,0.18)); position: relative; z-index: 2; }
  .lp .hero-figure::before { content: ""; position: absolute; bottom: 0; left: 50%; transform: translateX(-50%); width: 340px; height: 340px; border-radius: 50%; background: radial-gradient(circle, rgba(30,58,95,0.10), transparent 70%); z-index: 1; }
  .lp .float-card { position: absolute; background: var(--white); border: 1px solid var(--line); border-radius: 14px; padding: 12px 16px; box-shadow: 0 14px 30px rgba(23,59,52,0.12); font-size: 13px; font-weight: 600; display: flex; align-items: center; gap: 10px; z-index: 3; }
  .lp .float-card.fc1 { top: 8%; left: -6%; }
  .lp .float-card.fc2 { bottom: 14%; right: -4%; }
  .lp .float-card .ico { width: 26px; height: 26px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 14px; flex-shrink: 0; }
  .lp .fc1 .ico { background: var(--blue-pale); }
  .lp .fc2 .ico { background: #FCF3DC; }

  /* SECTIONS */
  .lp section { padding: 96px 0; }
  .lp .eyebrow { text-transform: uppercase; letter-spacing: .12em; font-size: 12.5px; font-weight: 700; color: var(--gold); margin-bottom: 14px; display: flex; align-items: center; gap: 10px; }
  .lp .eyebrow::before { content: ""; width: 18px; height: 2px; background: var(--gold); display: inline-block; }
  .lp .section-head { max-width: 640px; margin-bottom: 54px; }
  .lp .section-head h2 { font-size: clamp(28px, 3.6vw, 40px); line-height: 1.12; margin-bottom: 16px; }
  .lp .section-head p { font-size: 16.5px; color: var(--ink-soft); line-height: 1.6; }

  /* PAIN */
  .lp .pain { background: var(--navy-deep); color: var(--white); }
  .lp .pain .eyebrow { color: #fff; }
  .lp .pain .eyebrow::before { background: var(--gold); }
  .lp .pain .section-head h2 { color: var(--white); }
  .lp .pain .section-head p { color: #AEC0CF; }
  .lp .pain-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 18px; }
  .lp .pain-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.10); border-radius: var(--radius); padding: 26px 24px; }
  .lp .pain-card .q { font-size: 26px; font-family: 'Playfair Display', serif; color: var(--gold); margin-bottom: 10px; display: block; }
  .lp .pain-card p { font-size: 15px; line-height: 1.55; color: #D7E1E8; }

  /* METHOD */
  .lp .method-grid { display: grid; grid-template-columns: 0.85fr 1.15fr; gap: 54px; align-items: center; }
  .lp .method-figure { position: relative; display: flex; justify-content: center; }
  .lp .method-figure img { width: 100%; max-width: 380px; filter: drop-shadow(0 24px 30px rgba(23,59,52,0.14)); }
  .lp .quote-card { position: absolute; bottom: -6%; left: -8%; background: var(--white); border: 1px solid var(--line); border-radius: 16px; padding: 18px 20px; max-width: 230px; box-shadow: 0 16px 34px rgba(23,59,52,0.14); font-size: 14px; line-height: 1.5; }
  .lp .quote-card .name { font-weight: 700; color: var(--navy-deep); font-size: 13px; margin-top: 8px; }
  .lp .steps { list-style: none; display: flex; flex-direction: column; gap: 0; }
  .lp .step { display: flex; gap: 18px; padding: 22px 0; border-bottom: 1px solid var(--line); }
  .lp .step:last-child { border-bottom: none; }
  .lp .step-num { font-family: 'Playfair Display', serif; font-weight: 600; font-size: 15px; color: var(--gold); width: 38px; height: 38px; border-radius: 50%; border: 1.5px solid var(--gold-soft); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .lp .step h3 { font-size: 18px; margin-bottom: 6px; color: var(--navy-deep); }
  .lp .step p { font-size: 14.5px; color: var(--ink-soft); line-height: 1.55; }

  /* MODULES */
  .lp .modules { background: var(--blue-pale); }
  .lp .module-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
  .lp .module-card { background: var(--white); border-radius: var(--radius); padding: 28px 24px; border: 1px solid var(--line); transition: transform .15s ease, box-shadow .15s ease; }
  .lp .module-card:hover { transform: translateY(-3px); box-shadow: 0 16px 30px rgba(23,59,52,0.08); }
  .lp .module-card.lea-card { border: 1.5px solid var(--gold-soft); background: linear-gradient(135deg, #FFF8F6, #fff); }
  .lp .module-ico { width: 44px; height: 44px; border-radius: 12px; background: var(--navy-deep); display: flex; align-items: center; justify-content: center; margin-bottom: 18px; font-size: 19px; }
  .lp .module-ico.gold-ico { background: #FCF3DC; }
  .lp .module-card h3 { font-size: 17px; margin-bottom: 8px; }
  .lp .module-card p { font-size: 14px; color: var(--ink-soft); line-height: 1.55; }
  .lp .lea-link { color: var(--navy); font-weight: 600; text-decoration: underline; }

  /* GUIDE */
  .lp .guide-card { background: var(--navy); border-radius: 28px; overflow: hidden; display: grid; grid-template-columns: 0.9fr 1.1fr; align-items: stretch; }
  .lp .guide-figure { position: relative; display: flex; align-items: flex-end; justify-content: center; background: var(--blue-pale); padding-top: 34px; }
  .lp .guide-figure img { width: 78%; max-width: 300px; }
  .lp .guide-text { padding: 54px 50px 54px 10px; color: var(--white); }
  .lp .guide-text .eyebrow { color: var(--gold); }
  .lp .guide-text .eyebrow::before { background: var(--gold); }
  .lp .guide-text blockquote { font-family: 'Playfair Display', serif; font-style: italic; font-weight: 500; font-size: clamp(19px, 2.4vw, 25px); line-height: 1.42; margin: 14px 0 22px; color: #F4F7F9; }
  .lp .guide-text .signature { display: flex; align-items: center; gap: 12px; }
  .lp .guide-text .signature .name { font-weight: 700; font-size: 15px; }
  .lp .guide-text .signature .role { font-size: 13px; color: #AEC0CF; }
  .lp .sig-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--gold); flex-shrink: 0; }

  /* OFFER */
  .lp .offer { background: linear-gradient(180deg, #ffffff 0%, var(--blue-pale) 100%); }
  .lp .offer-card { background: var(--navy-deep); border-radius: 32px; padding: 64px 56px; color: var(--white); position: relative; overflow: hidden; display: grid; grid-template-columns: 1.2fr 0.8fr; gap: 40px; align-items: center; }
  .lp .offer-card::after { content: ""; position: absolute; top: -40%; right: -10%; width: 420px; height: 420px; background: radial-gradient(circle, rgba(228,87,46,0.20), transparent 65%); pointer-events: none; }
  .lp .offer-left .eyebrow { color: var(--gold); }
  .lp .offer-left .eyebrow::before { background: var(--gold); }
  .lp .offer-left h2 { color: var(--white); font-size: clamp(26px, 3.4vw, 36px); margin-bottom: 16px; line-height: 1.14; }
  .lp .offer-left p { color: #C6D4DE; font-size: 15.5px; line-height: 1.6; max-width: 440px; margin-bottom: 26px; }
  .lp .offer-list { list-style: none; display: flex; flex-direction: column; gap: 10px; margin-bottom: 30px; }
  .lp .offer-list li { display: flex; align-items: center; gap: 10px; font-size: 14.5px; color: #E4ECF0; }
  .lp .offer-num { position: relative; z-index: 2; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.16); border-radius: 22px; padding: 34px 28px; text-align: center; }
  .lp .offer-num .big { font-family: 'Playfair Display', serif; font-size: 74px; line-height: 1; color: var(--gold); font-weight: 600; }
  .lp .offer-num .unit { font-size: 15px; color: #D7E1E8; margin-top: 6px; font-weight: 600; }
  .lp .offer-num .small { font-size: 12.5px; color: #9FB2C0; margin-top: 14px; line-height: 1.5; }

  /* FAQ */
  .lp .faq-list { max-width: 760px; }
  .lp .faq-item { border-bottom: 1px solid var(--line); }
  .lp .faq-q { width: 100%; background: none; text-align: left; padding: 22px 4px; display: flex; align-items: center; justify-content: space-between; gap: 20px; font-size: 16.5px; font-weight: 600; color: var(--navy-deep); font-family: 'Playfair Display', serif; cursor: pointer; border: none; }
  .lp .faq-plus { width: 26px; height: 26px; border-radius: 50%; border: 1.5px solid var(--navy); flex-shrink: 0; position: relative; transition: transform .25s ease, background .2s ease; }
  .lp .faq-plus::before, .lp .faq-plus::after { content: ""; position: absolute; background: var(--navy); }
  .lp .faq-plus::before { top: 50%; left: 6px; right: 6px; height: 1.5px; transform: translateY(-50%); }
  .lp .faq-plus::after { left: 50%; top: 6px; bottom: 6px; width: 1.5px; transform: translateX(-50%); transition: opacity .2s ease; }
  .lp .faq-item.open .faq-plus { background: var(--navy); transform: rotate(180deg); }
  .lp .faq-item.open .faq-plus::before { background: var(--white); }
  .lp .faq-item.open .faq-plus::after { opacity: 0; }
  .lp .faq-a { max-height: 0; overflow: hidden; transition: max-height .3s ease; }
  .lp .faq-a p { padding: 0 34px 22px 4px; font-size: 15px; color: var(--ink-soft); line-height: 1.6; }

  /* FINAL CTA */
  .lp .final-cta { text-align: center; background: var(--blue-pale); padding: 96px 0; }
  .lp .final-cta h2 { font-size: clamp(28px, 4vw, 44px); max-width: 640px; margin: 0 auto 18px; }
  .lp .final-cta p { color: var(--ink-soft); font-size: 16.5px; max-width: 480px; margin: 0 auto 34px; }

  /* FOOTER */
  .lp footer { background: var(--navy-deep); color: #AEC0CF; padding: 56px 0 28px; }
  .lp .footer-top { display: flex; justify-content: space-between; flex-wrap: wrap; gap: 40px; margin-bottom: 40px; }
  .lp .footer-brand p { font-size: 13.5px; color: #8FA4B4; margin-top: 12px; max-width: 260px; line-height: 1.6; }
  .lp .footer-cols { display: flex; gap: 64px; flex-wrap: wrap; }
  .lp .footer-col h4 { font-size: 13px; text-transform: uppercase; letter-spacing: .08em; color: #7E94A5; margin-bottom: 14px; }
  .lp .footer-col a { display: block; font-size: 14px; color: #C6D4DE; margin-bottom: 10px; }
  .lp .footer-bottom { border-top: 1px solid rgba(255,255,255,0.08); padding-top: 22px; font-size: 12.5px; color: #7E94A5; display: flex; justify-content: space-between; flex-wrap: wrap; gap: 10px; }

  /* REVEAL */
  .lp .reveal { opacity: 0; transform: translateY(18px); transition: opacity .6s ease, transform .6s ease; }
  .lp .reveal.in { opacity: 1; transform: translateY(0); }

  /* RESPONSIVE */
  @media (max-width: 900px) {
    .lp .hero { padding: 96px 0 0; }
    .lp .hero-grid { grid-template-columns: 1fr; gap: 8px; }
    .lp .hero p.lead { max-width: 100%; }
    .lp .hero-figure { max-width: 280px; margin: 0 auto; }
    .lp .hero-figure img { max-width: 240px; }
    .lp .float-card { display: none; }
    .lp .method-grid { grid-template-columns: 1fr; gap: 44px; }
    .lp .quote-card { display: none; }
    .lp .method-figure img { max-width: 260px; }
    .lp .guide-card { grid-template-columns: 1fr; border-radius: 22px; }
    .lp .guide-figure { padding-top: 24px; }
    .lp .guide-figure img { width: 48%; min-width: 160px; }
    .lp .guide-text { padding: 36px 26px 42px; }
    .lp .offer-card { grid-template-columns: 1fr; padding: 42px 26px; border-radius: 24px; }
  }
  @media (max-width: 860px) {
    .lp .module-grid { grid-template-columns: 1fr 1fr; }
  }
  @media (max-width: 760px) {
    .lp .nav-links { display: none; }
    .lp .btn-ghost { display: none; }
    .lp section { padding: 64px 0; }
    .lp .section-head { margin-bottom: 38px; }
    .lp .pain-grid { grid-template-columns: 1fr; }
    .lp .module-grid { grid-template-columns: 1fr; }
    .lp .footer-top { flex-direction: column; gap: 30px; }
    .lp .footer-cols { gap: 36px; }
  }
  @media (max-width: 560px) {
    .lp .btn-gold { padding: 10px 16px; font-size: 13.5px; }
    .lp .nav-inner { height: 58px; }
    .lp .logo-word { display: none; }
  }
`;

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="8" fill="#E4572E"/>
      <path d="M4.5 8.2l2.3 2.3 4.7-4.7" stroke="#F6F2EC" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function FaqItem({ question, answer }) {
  const [open, setOpen] = React.useState(false);
  const aRef = useRef(null);

  useEffect(() => {
    if (aRef.current) {
      aRef.current.style.maxHeight = open ? aRef.current.scrollHeight + 'px' : '0';
    }
  }, [open]);

  return (
    <div className={`faq-item${open ? ' open' : ''}`}>
      <button className="faq-q" onClick={() => setOpen(!open)}>
        {question}
        <span className="faq-plus"></span>
      </button>
      <div className="faq-a" ref={aRef}>
        <p>{answer}</p>
      </div>
    </div>
  );
}

export function LandingPage() {
  const navigate = useNavigate();

  useEffect(() => {
    const els = document.querySelectorAll('.lp .reveal');
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } });
      }, { threshold: 0.12 });
      els.forEach(el => io.observe(el));
      return () => io.disconnect();
    } else {
      els.forEach(el => el.classList.add('in'));
    }
  }, []);

  return (
    <div className="lp">
      <style>{styles}</style>

      {/* NAV */}
      <nav className="nav">
        <div className="nav-inner">
          <a href="#top" className="logo">
            <img src={logoBrandStarter} alt="BrandStarter" style={{height:'36px',width:'auto'}}/>
          </a>
          <div className="nav-links">
            <a className="nav-link" href="#probleme">Le problème</a>
            <a className="nav-link" href="#methode">La méthode</a>
            <a className="nav-link" href="#modules">Modules</a>
            <a className="nav-link" href="#offre">Offre</a>
            <a className="nav-link" href="#faq">FAQ</a>
          </div>
          <div className="nav-actions">
            <button className="btn-ghost" onClick={() => navigate('/connexion')}>Connexion</button>
            <button className="btn-gold" onClick={() => navigate('/inscription')}>Demander mon accès · 20 jours gratuits</button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <header className="hero" id="top">
        <div className="hero-grid">
          <div className="hero-text">
            <span className="badge"><span className="dot"></span>20 jours offerts par Stratvente · sans carte bancaire</span>
            <h1>
              Ton activité mérite une{' '}
              <span className="underline">
                image de marque
                <svg viewBox="0 0 280 10" preserveAspectRatio="none">
                  <path d="M2 7 C 80 1, 200 1, 278 7" stroke="#E4572E" strokeWidth="4" fill="none" strokeLinecap="round"/>
                </svg>
              </span>{' '}
              qui lui ressemble vraiment.
            </h1>
            <p className="lead">BrandStarter te guide module par module pour construire ton positionnement et ton identité de marque : du message que tu portes jusqu'à ta présence visuelle et ton discours en ligne. Sans agence, sans budget à 3 000€, sans attente de six mois.</p>
            <div className="hero-ctas">
              <button className="btn-navy" onClick={() => navigate('/inscription')}>
                Demander mon accès <ArrowIcon />
              </button>
              <a href="#methode" className="btn-outline">Voir comment ça marche</a>
            </div>
            <div className="hero-sub">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8.5l3.2 3.2L13 5" stroke="#1E3A5F" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              Déjà adopté par les premiers créateurs testeurs de Stratvente
            </div>
          </div>
          <div className="hero-figure">
            <div className="float-card fc1"><span className="ico">🧭</span> Positionnement et promesse</div>
            <img src={claraOuvert} alt="Clara, ta guide BrandStarter" />
            <div className="float-card fc2"><span className="ico">✓</span> Module 3/5 terminé</div>
          </div>
        </div>
      </header>

      {/* PAIN */}
      <section className="pain" id="probleme">
        <div className="wrap">
          <div className="section-head reveal">
            <div className="eyebrow">Tu reconnais ce moment ?</div>
            <h2>La frustration de tous les jeunes créateurs</h2>
            <p>Tu as lancé ton activité avec l'énergie qu'il fallait. Mais ton image de marque, elle, n'a jamais suivi.</p>
          </div>
          <div className="pain-grid">
            <div className="pain-card reveal"><span className="q">"</span><p>Tu changes de direction visuelle tous les trois mois sans jamais te sentir vraiment représenté.</p></div>
            <div className="pain-card reveal"><span className="q">"</span><p>Tes posts Instagram n'ont jamais la même couleur, la même police, le même ton.</p></div>
            <div className="pain-card reveal"><span className="q">"</span><p>Les agences de branding démarrent à 3 000 € pour un client qui vient à peine de se lancer.</p></div>
            <div className="pain-card reveal"><span className="q">"</span><p>Tu sais, au fond, que tu n'as pas encore de réponse claire à la question : pourquoi toi plutôt qu'un autre ?</p></div>
          </div>
        </div>
      </section>

      {/* METHODE */}
      <section id="methode">
        <div className="wrap">
          <div className="method-grid">
            <div className="method-figure reveal">
              <img src={claraLeve} alt="Clara présente la méthode BrandStarter" />
              <div className="quote-card">
                « Pas besoin de savoir designer. Je t'indique exactement quoi décider, dans quel ordre. »
                <div className="name">Clara, ta guide BrandStarter</div>
              </div>
            </div>
            <div>
              <div className="section-head reveal">
                <div className="eyebrow">La méthode BrandStarter</div>
                <h2>On construit ta marque ensemble, étape par étape</h2>
                <p>Un parcours guidé par l'IA, pensé pour les créateurs et indépendants qui n'ont ni le temps ni le budget d'une agence.</p>
              </div>
              <ul className="steps">
                {[
                  ['Tu réponds à quelques questions', 'Sur ton activité, ta cible, ce que tu veux dégager. 10 minutes, pas plus.'],
                  ["L'IA te propose une direction", 'Positionnement, persona, palette, ton de voix : tout tient ensemble, tout se justifie.'],
                  ['Tu ajustes, module par module', 'Positionnement, univers visuel, ton de voix : tu valides ou tu affines, à ton rythme.'],
                  ['Tu repars avec ta charte complète', 'Prête à l\'emploi, exportable, déjà appliquée à tes premiers supports.'],
                ].map(([title, desc], i) => (
                  <li className="step reveal" key={i}>
                    <span className="step-num">{i + 1}</span>
                    <div><h3>{title}</h3><p>{desc}</p></div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section className="modules" id="modules">
        <div className="wrap">
          <div className="section-head reveal">
            <div className="eyebrow">Ce que tu obtiens</div>
            <h2>5 modules guidés, un seul fil conducteur</h2>
            <p>Chaque module s'appuie sur le précédent. Tu pars de qui tu es et pourquoi tu le fais, pour arriver à une identité complète qui tient la route.</p>
          </div>
          <div className="module-grid">
            {[
              ['🎯', 'Positionnement & persona', "Qui tu sers vraiment, ce qui te différencie, ta raison d'être. La base de tout le reste."],
              ['🎨', 'Univers visuel', 'Palette, typographies et codes graphiques pensés pour ton secteur.'],
              ['✦', 'Positionnement et promesse', 'Tes alternatives, ta différence et ta phrase de positionnement, prêtes à utiliser.'],
              ['💬', 'Ton de voix', 'La façon dont ta marque parle, à l\'écrit comme à l\'oral, partout pareil.'],
              ['📂', 'Charte exportable', 'Toute ta marque réunie dans un document clair, prêt à partager.'],
            ].map(([ico, title, desc]) => (
              <div className="module-card reveal" key={title}>
                <div className="module-ico">{ico}</div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
            <div className="module-card lea-card reveal">
              <div className="module-ico gold-ico">✦</div>
              <h3>Réseaux sociaux</h3>
              <p>Ta stratégie social media se construit dans <strong>Léa</strong>, l'app dédiée de Stratvente, pensée pour aller plus loin sur tes réseaux une fois ta marque posée. <a href="https://plum-jackal-784583.hostingersite.com" target="_blank" rel="noreferrer" className="lea-link">Découvrir Léa →</a></p>
            </div>
          </div>
        </div>
      </section>

      {/* GUIDE */}
      <section className="guide">
        <div className="wrap">
          <div className="guide-card reveal">
            <div className="guide-figure">
              <img src={claraCroise} alt="Clara, ta guide BrandStarter" />
            </div>
            <div className="guide-text">
              <div className="eyebrow">Une méthode humaine, pas un outil froid</div>
              <blockquote>« Beaucoup de créateurs sautent directement au visuel sans avoir posé les bases. Je suis là pour t'aider à définir d'abord qui tu es, pour qui tu travailles, et pourquoi tu le fais mieux que les autres. »</blockquote>
              <div className="signature">
                <span className="sig-dot"></span>
                <div>
                  <div className="name">Clara</div>
                  <div className="role">Guide BrandStarter, by Stratvente</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OFFER */}
      <section className="offer" id="offre">
        <div className="wrap">
          <div className="offer-card reveal">
            <div className="offer-left">
              <div className="eyebrow">L'offre de lancement</div>
              <h2>20 jours offerts par Stratvente. Accès complet, sans carte bancaire.</h2>
              <p>Le temps de poser ton positionnement, de clarifier ton persona, de construire une identité qui tient. Pas un essai limité : un vrai parcours, complet, dès le premier jour.</p>
              <ul className="offer-list">
                <li><CheckIcon /> Les 5 modules débloqués dès le premier jour</li>
                <li><CheckIcon /> Aucune carte bancaire requise à l'inscription</li>
                <li><CheckIcon /> Ta charte t'appartient, même si tu t'arrêtes après</li>
              </ul>
              <button className="btn-gold" onClick={() => navigate('/inscription')}>Demander mon accès</button>
            </div>
            <div className="offer-num">
              <div className="big">20</div>
              <div className="unit">jours gratuits</div>
              <div className="small">offerts par Stratvente<br/>aux nouveaux créateurs</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq">
        <div className="wrap">
          <div className="section-head reveal">
            <div className="eyebrow">Questions fréquentes</div>
            <h2>Tout ce qu'on te demande avant de te lancer</h2>
          </div>
          <div className="faq-list">
            {[
              ["C'est vraiment gratuit pendant 20 jours ?", "Oui. Aucune carte bancaire n'est demandée à l'inscription. Tu accèdes à l'ensemble des modules dès le premier jour, sans limitation de fonctionnalités."],
              ["Ai-je besoin de compétences en design ?", "Non. BrandStarter est pensé pour celles et ceux qui n'ont pas de background en design ou en marketing. Tu réponds à des questions, Clara structure ta réflexion, et tout prend forme progressivement."],
              ["Qu'est-ce que j'obtiens à la fin du parcours ?", "Une charte d'identité complète et exportable : positionnement, logo et déclinaisons, palette, typographies, ton de voix et gabarits réseaux sociaux."],
              ["Et après les 20 jours ?", "Tu choisis librement si tu poursuis sur une formule payante. Ta charte déjà construite reste disponible et t'appartient dans tous les cas."],
              ["Mon entreprise n'est pas encore créée, c'est un problème ?", "Pas du tout. C'est même le bon moment : tu démarres avec une identité claire plutôt que de devoir tout reprendre plus tard."],
            ].map(([q, a]) => (
              <FaqItem key={q} question={q} answer={a} />
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="final-cta" id="inscription">
        <div className="wrap reveal">
          <h2>Arrête d'improviser ton image. Construis une marque qui te ressemble.</h2>
          <p>20 jours offerts, accès complet à tous les modules, aucune carte bancaire requise.</p>
          <button className="btn-navy" onClick={() => navigate('/inscription')}>
            Demander mon accès <ArrowIcon />
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer>
        <div className="wrap">
          <div className="footer-top">
            <div className="footer-brand">
              <a href="#top" className="logo">
                <img src={logoBrandStarter} alt="BrandStarter" style={{height:'32px',width:'auto',filter:'brightness(0) invert(1)'}}/>
              </a>
              <p>BrandStarter est conçu et opéré par Stratvente, pour les indépendants et TPE des Hauts-de-France et d'ailleurs.</p>
            </div>
            <div className="footer-cols">
              <div className="footer-col">
                <h4>Plateforme</h4>
                <a href="#probleme">Le problème</a>
                <a href="#methode">La méthode</a>
                <a href="#modules">Modules</a>
              </div>
              <div className="footer-col">
                <h4>Compte</h4>
                <button className="btn-ghost" style={{padding:0, fontSize:'14px', color:'#C6D4DE', display:'block', marginBottom:'10px', textAlign:'left'}} onClick={() => navigate('/connexion')}>Connexion</button>
                <button className="btn-ghost" style={{padding:0, fontSize:'14px', color:'#C6D4DE', display:'block', textAlign:'left'}} onClick={() => navigate('/inscription')}>Demander mon accès</button>
              </div>

            </div>
          </div>
          <div className="footer-bottom">
            <span>© 2026 Stratvente · BrandStarter. Tous droits réservés.</span>
            <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
              <a href="/mentions-legales" style={{color:'#9AB0C4',fontSize:12,textDecoration:'none'}}>Mentions légales</a>
              <a href="/confidentialite" style={{color:'#9AB0C4',fontSize:12,textDecoration:'none'}}>Politique de confidentialité</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
